/**
 * The edited-grid cache in `sessionStorage` holds only the rows that carry an
 * edit. A large input (NTV) has too many rows and period columns to store whole
 * (`QuotaExceededError`); the unedited rows are rebuilt from the loaded data.
 */
interface IGridRow {
  Input_RowID: string;
  updatedCells: string[];
}

export const getModifiedRows = <T extends IGridRow>(rows: T[]): T[] =>
  rows.filter((row) => row.updatedCells.length > 0);

/** `baseRows` with each stored edited row put in place of the row with the same `Input_RowID`. */
export const mergeModifiedRows = <T extends IGridRow>(
  baseRows: T[],
  stored: string | null | undefined
): T[] => {
  if (!stored) {
    return baseRows;
  }
  const modifiedById = new Map(
    (JSON.parse(stored) as T[]).map((row) => [row.Input_RowID, row])
  );
  return baseRows.map((row) => modifiedById.get(row.Input_RowID) ?? row);
};

/**
 * Store the edited rows under `key`. If the browser refuses (quota), the key
 * is removed so a later restore never loads an older, stale copy.
 */
export const persistModifiedRows = (key: string, rows: IGridRow[]): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(getModifiedRows(rows)));
  } catch (error) {
    sessionStorage.removeItem(key);
    // eslint-disable-next-line no-console
    console.warn(
      `Edited rows not cached (${(error as Error).name}); a reload of this input starts from the saved data.`
    );
  }
};
